import type { ApiClient } from "@commerce-os/api-client";
import type { QueryClient } from "@tanstack/react-query";
import type { i18n } from "i18next";
import { createContext, type ReactNode, useContext } from "react";

import type { Realtime } from "./realtime";
import type { SearchProducts } from "./search";
import type { Session } from "./session";
import type { UploadFile } from "./upload";

/** What every route and component may use: the typed API client, the query cache, the session and i18n. */
export interface AppContext {
  api: ApiClient;
  queryClient: QueryClient;
  session: Session;
  i18n: i18n;
  /** Uploads a file through the files flow and resolves once it is ready. */
  uploadFile: UploadFile;
  /** Searches products in Meilisearch with a tenant search token. */
  searchProducts: SearchProducts;
  /** Change signals from the API (dashboard live refresh). */
  realtime: Realtime;
}

const Context = createContext<AppContext | null>(null);

export function AppContextProvider({ value, children }: { value: AppContext; children: ReactNode }) {
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useApp(): AppContext {
  const value = useContext(Context);
  if (value === null) throw new Error("useApp outside AppContextProvider");
  return value;
}
